import React, { useMemo } from 'react';
import { ArrowDownTrayIcon, ArrowLeftIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import ChartCard, { ChartCardState } from '@ui/chart/ChartCard';
import DataTable, { DataTableRow, DataTableSort } from '@ui/chart/DataTable';
import KpiStrip from '@ui/chart/KpiStrip';
import ChartMessage from '@ui/chart/ChartMessage';
import { CHART_HEIGHT } from '@ui/chart/chartTokens';
import BarChart from '@ui/chart/BarChart';
import LineChart from '@ui/chart/LineChart';
import { barChartTable, lineChartTable } from '@ui/chart/chartTable';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import CheckboxMenu from '@ui/menu/CheckboxMenu';
import ToolbarButton from '@ui/button/ToolbarButton';
import { MonthRange } from '@ui/selector/MonthRangePicker';
import { RatePlanEvolution, RatePlanReport, RevenueScope, VatBasis } from '@app/model/bi/Bi';
import { formatDate, MONTH_LABELS, missingNote, missingOf, revenueContent, revenueName } from '../Bi.utilities';
import {
	ColumnSet, columnsOf, EvolutionLine, EvolutionMeasure, evolutionByRow, evolutionLines, focusFigures, GroupBy, mixSeries, OTHER_LINE, pointsOf,
	RatePlanNode, RatePlanTree, rowLimitOf, tableRows, totalRow,
} from './RatePlans.utilities';

// The period of the page in words: "Jan – Dec 2026", and its first and last night (RQ-19).
const lastDay = (yearMonth: string) => new Date(Date.UTC(Number(yearMonth.slice(0, 4)), Number(yearMonth.slice(5, 7)), 0)).toISOString().slice(0, 10);
export const periodLabel = (period: MonthRange): string => {
	const m = (ym: string) => MONTH_LABELS[Number(ym.slice(5, 7)) - 1];
	if (period.from === period.to) return `${m(period.from)} ${period.from.slice(0, 4)}`;
	return period.from.slice(0, 4) === period.to.slice(0, 4) ? `${m(period.from)} – ${m(period.to)} ${period.to.slice(0, 4)}`
		: `${m(period.from)} ${period.from.slice(0, 4)} – ${m(period.to)} ${period.to.slice(0, 4)}`;
};
export const nightsLabel = (period: MonthRange): string => `${formatDate(`${period.from}-01`)} – ${formatDate(lastDay(period.to))}`;

export const COLUMN_SETS: { value: ColumnSet, label: string }[] = [
	{ value: 'ly', label: 'Previous year (STLY, LY actual, Δ%)' },
	{ value: 'd1', label: 'Situation D-1' }, { value: 'd3', label: 'Situation D-3' }, { value: 'd7', label: 'Situation D-7' },
	{ value: 'd14', label: 'Situation D-14' }, { value: 'd30', label: 'Situation D-30' },
	{ value: 'pu1', label: 'PU 1' }, { value: 'pu3', label: 'PU 3' }, { value: 'pu7', label: 'PU 7' }, { value: 'pu14', label: 'PU 14' }, { value: 'pu30', label: 'PU 30' },
];

// --- the revenue mix (direction B) ------------------------------------------------------------------------------------

type MixProps = { report: RatePlanReport | undefined, by: 'POLICY' | 'BOARD', onBy: (by: 'POLICY' | 'BOARD') => void, period: MonthRange,
	scope: RevenueScope, vat: VatBasis, state: ChartCardState, onRetry: () => void, lastYearNote: string | null, error: string | null };

// The share of the revenue of each group this year, at STLY and at LY actual: one stacked bar each, 0–100 %.
export const RatePlanMixCard: React.FunctionComponent<MixProps> = ({ report, by, onBy, period, scope, vat, state, onRetry, lastYearNote, error }) => {
	const series = useMemo(() => report === undefined ? [] : mixSeries(report, by, scope, vat), [report, by, scope, vat]);
	const categories = ['This year', 'STLY', 'LY actual'];
	const empty = report !== undefined && report.parts.length === 0;
	return <ChartCard title={`Revenue mix by ${by === 'POLICY' ? 'refund policy' : 'board'}`} period={`${periodLabel(period)} · this year, STLY and LY actual`}
		state={empty ? 'empty' : state} onRetry={onRetry} emptyLabel="Nothing on the books for this period, this year or last."
		errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		actions={<ButtonTabs label="Mix by" value={by} onChange={v => onBy(v as 'POLICY' | 'BOARD')}
			options={[{ value: 'POLICY', label: 'Refund policy' }, { value: 'BOARD', label: 'Board' }]} />}
		table={<DataTable {...barChartTable({ categories, series, categoryLabel: 'Period', format: 'percent', decimals: 1 })} framed={false} ariaLabel="Revenue mix" />}
		caption={<>Share of the {revenueName(scope, vat)} of each group. The groups follow the rate plan name (NR, Flex, BB, RO …).{lastYearNote === null ? '' : ` ${lastYearNote}`}</>}>
		<BarChart categories={categories} series={series} stacked max={1} format="percent" decimals={1} ariaLabel={`Revenue mix by ${by === 'POLICY' ? 'refund policy' : 'board'}`} />
	</ChartCard>;
};

// --- the table (RQ-10 … RQ-19) ----------------------------------------------------------------------------------------

type TableProps = {
	tree: RatePlanTree | undefined, by: GroupBy, period: MonthRange, sets: ColumnSet[], onToggle: (set: ColumnSet) => void,
	scope: RevenueScope, vat: VatBasis, state: ChartCardState, onRetry: () => void, resetKey: string,
	lastYearNote: string | null,
	// ROO2-271 D1: the scope has a property with no stays in the last-year period (the like-for-like columns).
	lfl: boolean,
	// The Excel file (ROO2-261): onExport downloads it; exporting = the file loads ("Preparing…"); exportReady = the table
	// shows its period (not while it loads, refreshes or failed).
	onExport: () => void, exporting: boolean, exportReady: boolean,
	error: string | null,
	// ROO2-267: the BUILD-UP column (the line of each row over the window of the build-up chart).
	buildUp?: { block: string, label: string, of: (node: RatePlanNode) => (number | null)[] | null },
	// RQ-22: a click on a group or rate plan name focuses the chart on that row; a second click returns to all lines.
	onSelect?: (row: DataTableRow, level: number) => void,
	selectedId?: string,
	// RQ-20: with None, the chart follows the order of the table after a sort.
	onSortChange?: (sort: DataTableSort | undefined, topRowIds: string[]) => void,
	// ROO2-315 part c2 (Q4): the ADR and the report rules of the scope, once (adrRulesNote); null without rules.
	rulesNote?: string | null,
	// ROO2-302 (Q1 C): the note that the held rooms of group blocks are not in this report yet (heldBlocksNote); null without them.
	heldNote?: string | null,
};

export const RatePlanTableCard: React.FunctionComponent<TableProps> = ({
	tree, by, period, sets, onToggle, scope, vat, state, onRetry, resetKey, lastYearNote, lfl, error, onExport, exporting, exportReady,
	buildUp, onSelect, selectedId, onSortChange, rulesNote = null, heldNote = null,
}) => {
	const { columns, groups } = useMemo(() => columnsOf(sets, buildUp ?? null, lfl), [sets, buildUp, lfl]);
	const rows = useMemo(() => tree === undefined ? [] : tableRows(tree, scope, vat, buildUp?.of), [tree, scope, vat, buildUp]);
	const grouped = by !== 'NONE';
	const plans = tree?.plans ?? 0;
	const empty = tree !== undefined && plans === 0;
	const missing = tree === undefined ? null : missingNote(missingOf([tree.total.cuts[0], tree.total.stly, tree.total.ly], scope, vat), '–', 'the period');
	return <ChartCard title={`${grouped ? 'Rate plan groups' : 'Rate plans'} · ${periodLabel(period)}`}
		period={`Stay dates ${nightsLabel(period)} · past nights actual, later nights on the books`}
		info={{ title: 'Rate plan groups', content: <>Non-refundable: NR, NRF, NRFL, non ref, no ref, not refund, pre pay, advance pay. Promotions &amp; offers:
			promo, early book, early bird, last minute, min stay, offer…, deal…, special…, package…. Flexible: flex…, BAR, STD, refund, standard, cancel.
			The first rule that matches decides. Board: BB, HB, FB, B&amp;B, breakfast, colazione, half board, full board, pens… = Breakfast or meals;
			RO, room only, solo camera, no breakfast = Room only; with no board word, the board of the night in the PMS.</> }}
		state={empty ? 'empty' : state} onRetry={onRetry} emptyLabel="Nothing on the books for this period, this year or last."
		errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		actions={<>
			{/* ToolbarButton (design system v1.19.0): the 36 px look of the Columns button beside it, as the Segmentation export. */}
			{plans > 0 && <ToolbarButton icon={<ArrowDownTrayIcon className="h-4 w-4" />} disabled={!exportReady} onClick={onExport}>
				{exporting ? 'Preparing…' : 'Export'}
			</ToolbarButton>}
			<CheckboxMenu label={<><TableCellsIcon aria-hidden="true" className="h-4 w-4 text-gray-400" />Columns<span className="font-normal text-gray-500">· {sets.length}</span></>}
				options={COLUMN_SETS} selected={sets} onToggle={onToggle} anchor="bottom end" variant="button" />
		</>}
		caption={<>
			Total = the total of the Segmentation report for the same stay dates. RN = sold room nights; REV = {revenueName(scope, vat)} ({revenueContent(scope)}).
			STLY = the same stay dates last year, as they stood one year ago today; LY actual = as they stand today. Δ% compares revenue.
			PU n = today − situation D-n; ADR Δ = ADR today − ADR at situation D-n. ADR includes the €0 nights (complimentary, house use).
			Groups follow the rate plan name (NR, Flex, BB, RO …); with no board word in the name, the board of the night in the PMS.
			{lastYearNote === null ? '' : ` ${lastYearNote}`}{missing === null ? '' : ` ${missing}`}
			{rulesNote === null ? null : <><br />{rulesNote}</>}
			{heldNote === null ? null : <><br />{heldNote}</>}
		</>}>
		{tree !== undefined && <DataTable key={resetKey} ariaLabel={grouped ? 'Rate plan groups' : 'Rate plans'} framed={false} stickyHeader columns={columns}
			columnGroups={groups} rows={rows} defaultSort={{ columnId: 'rev', direction: 'desc' }} sortScope={grouped ? 'children' : 'top'}
			sortCycle="threeClicks" rowLimit={rowLimitOf(by)} footer={plans > 1 ? totalRow(tree, scope, vat) : undefined}
			onRowSelect={onSelect} selectedRowId={selectedId} onSortChange={onSortChange} />}
	</ChartCard>;
};

// --- the build-up (ROO2-267, spec chapter 5) ---------------------------------------------------------------------------

type EvolutionProps = {
	evolution: RatePlanEvolution | undefined, tree: RatePlanTree | undefined, by: GroupBy, period: MonthRange,
	measure: EvolutionMeasure, onMeasure: (m: EvolutionMeasure) => void, window: number, onWindow: (w: number) => void,
	state: ChartCardState, error: string | null, onRetry: () => void,
	// RQ-20: the ids of the top rows in the order of the table (after a sort), or null for the order of the tree.
	order: string[] | null,
	// RQ-22: the focused row (a group, a rate plan, or "other"), and a click on a line or its legend item.
	focus: string | null, onFocus: (id: string | null) => void,
};

export const WINDOWS = [30, 90, 180, 365];

const FOCUS_FORMAT = { rn: 'number', rev: 'money', adr: 'money' } as const;

// One line for each of the first 6 rows of the table and a dashed "Other (N)" for the rest (RQ-20); days after the last night
// of the period are not drawn (RQ-21). With a focus (RQ-22): the row this year against last year (dashed), with the focus
// figures of M-13 above the chart.
export const RatePlanEvolutionCard = React.forwardRef<HTMLDivElement, EvolutionProps>(({
	evolution, tree, by, period, measure, onMeasure, window, onWindow, state, error, onRetry, order, focus, onFocus,
}, ref) => {
	const data = useMemo(() => {
		if (evolution === undefined || tree === undefined) return null;
		const lastNight = lastDay(period.to);
		const cut = (values: (number | null)[]) => values.map((v, i) => evolution.days[i] > lastNight ? null : v);
		const { lines, other } = evolutionLines(evolution, tree, by, order);
		const all = [...lines, ...(other === null ? [] : [other])];
		let focused: EvolutionLine | null = null;
		if (focus !== null) {
			focused = all.find(l => l.id === focus) ?? null;
			const row = evolutionByRow(evolution, by).get(focus);
			const node = [...tree.rows, ...tree.rows.flatMap(r => r.children)].find(r => r.id === focus);
			if (focused === null && row !== undefined && node !== undefined) focused = { id: focus, title: node.title, ...row };
		}
		const series = focused === null ? all.map(l => ({ id: l.id, label: l.title, values: cut(pointsOf(l.thisYear, measure)),
			...(l.id === OTHER_LINE ? { role: 'other' as const, dashed: true } : {}) }))
			: [{ id: 'thisYear', label: `${focused.title} · this year`, role: 'own' as const, values: cut(pointsOf(focused.thisYear, measure)) },
				{ id: 'lastYear', label: 'Last year (same lead time)', role: 'compset' as const, dashed: true, values: pointsOf(focused.lastYear, measure) }];
		return { series, days: evolution.days, focused };
	}, [evolution, tree, by, period, measure, order, focus]);
	const focusing = focus !== null;
	// A focus on a property row (T9): the chart has no line for one property.
	const noLine = focusing && data !== null && data.focused === null;
	const finished = data !== null && lastDay(period.to) < data.days[0];
	const nothing = data !== null && data.series.every(s => s.values.every(v => v === null || v === 0));
	// An empty card keeps the switches (its text asks for a wider window), so the body shows the message, not the card.
	const empty = finished || nothing;
	const format = measure === 'rn' ? 'number' : 'money';
	const decimals = measure === 'adr' ? 2 : 0;
	const name = data?.focused?.title ?? null;
	const groupWord = by === 'NONE' ? 'rate plan' : 'group';
	// As the chart and BUILD-UP (T17): the figures end at the last night of the period, both years on the same day.
	const f = data?.focused === null || data?.focused === undefined ? null : (() => {
		const lastNight = lastDay(period.to);
		const days = data.days.filter(d => d <= lastNight).length;
		return focusFigures(pointsOf(data.focused.thisYear, measure).slice(0, days), pointsOf(data.focused.lastYear, measure).slice(0, days), measure);
	})();
	// The switches sit over the chart and over the table, not in the header: on a phone the header has no room beside
	// Chart | Table (the selects were cut at 400 px). "← All" leads the row, above the figures (T8).
	const switches = <div className="mb-3 flex flex-wrap items-center gap-2">
		{focusing && <ToolbarButton icon={<ArrowLeftIcon className="h-4 w-4" />} onClick={() => onFocus(null)}>All</ToolbarButton>}
		<ButtonTabs label="Measure of the chart" value={measure} onChange={v => onMeasure(v as EvolutionMeasure)}
			options={[{ value: 'rn', label: 'RN' }, { value: 'rev', label: 'Revenue' }, { value: 'adr', label: 'ADR' }]} />
		<ButtonTabs label="Window of the chart" value={String(window)} onChange={v => onWindow(Number(v))}
			options={WINDOWS.map(w => ({ value: String(w), label: `${w} days` }))} />
	</div>;
	return <div ref={ref} className="scroll-mt-4"><ChartCard title={`${name !== null ? `How ${name} built up` : `How each ${groupWord} built up`} · ${periodLabel(period)}`}
		period={data === null ? 'As it stood on each day of the window' : `As it stood on each day ${formatDate(data.days[0])} – ${formatDate(data.days[data.days.length - 1])}`}
		state={empty && state !== 'error' ? (state === 'refreshing' ? 'refreshing' : 'default') : state} onRetry={onRetry}
		errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		table={data === null || noLine || empty ? undefined : <>{switches}<DataTable {...lineChartTable({ dates: data.days, series: data.series, format, decimals })}
			framed={false} size="normal" ariaLabel={name === null ? `How each ${groupWord} built up` : `How ${name} built up`} /></>}
		caption={focusing ? 'Last year = the same stay dates one year earlier, as they stood on the same day one year earlier. Select the name again or "All" to see all lines.'
			: 'One line for each of the first 6 rows of the table, in its order; Other = the rest added together (dashed). Click a row in the table or the legend to compare it with last year.'}>
		{switches}
		{empty && <ChartMessage height={CHART_HEIGHT.normal}>
			{finished ? 'This stay period had finished before the window starts. Widen the window.' : 'Nothing on the books in this window.'}
		</ChartMessage>}
		{!empty && f !== null && <div className="mb-4"><KpiStrip ariaLabel={`How ${name ?? ''} built up: figures`} items={[
			{ id: 'now', label: 'Now', value: f.now, format: FOCUS_FORMAT[measure], decimals,
				change: f.change === null ? undefined : { value: f.change, format: 'percent', label: 'vs last year at this point', upIsGood: null } },
			{ id: 'lastYear', label: 'LY at this point', value: f.lastYear, format: FOCUS_FORMAT[measure], decimals },
			...(measure === 'adr' ? [] : [
				{ id: 'week', label: 'Last 7 days', value: f.week, format: FOCUS_FORMAT[measure] },
				{ id: 'weekLastYear', label: 'Last 7 days, LY', value: f.weekLastYear, format: FOCUS_FORMAT[measure] },
			]),
		]} /></div>}
		{/* A property row: the text in place of the chart, under "← All" (the empty state of the card would hide the button). */}
		{!empty && noLine && <ChartMessage height={CHART_HEIGHT.normal}>The chart has no line for one property. Select its rate plan or its group.</ChartMessage>}
		{/* RQ-21: "As of" the date, and each line's value largest first. */}
		{data !== null && !empty && !noLine && <LineChart dates={data.days} series={data.series} format={format} decimals={decimals} tooltipPrefix="As of" tooltipOrder="value"
			ariaLabel={name === null ? `How each ${groupWord} built up` : `How ${name} built up`}
			onSeriesClick={id => { if (!focusing) onFocus(id); }} />}
	</ChartCard></div>;
});
RatePlanEvolutionCard.displayName = 'RatePlanEvolutionCard';

// M-12 for the BUILD-UP column: the line of a group or rate plan row over the window, in RN (also when the chart shows ADR)
// or in revenue. A property row has none. As in the chart, the days after the last night of the period have no value (T17):
// the line and its change end with the period.
export const buildUpLookup = (evolution: RatePlanEvolution | undefined, by: GroupBy, measure: EvolutionMeasure, period: MonthRange) => {
	if (evolution === undefined) return null;
	const m: EvolutionMeasure = measure === 'adr' ? 'rn' : measure;
	const byRow = evolutionByRow(evolution, by);
	const lastNight = lastDay(period.to);
	const cut = (values: (number | null)[]) => values.map((v, i) => evolution.days[i] > lastNight ? null : v);
	return {
		label: m === 'rn' ? 'RN' : 'REV',
		of: (node: RatePlanNode) => {
			if (node.kind === 'PROPERTY') return null;
			const row = byRow.get(node.id);
			return cut(row === undefined ? evolution.days.map(() => 0) : pointsOf(row.thisYear, m));
		},
	};
};
